export type ProductTierId = 'launch' | 'growth' | 'command'
export type BillingMode = 'monthly' | 'buyout'
export type OrderStatus = 'draft' | 'pending_payment' | 'paid' | 'active' | 'in_progress' | 'completed' | 'cancelled'
export type IntakeStatus = 'not_started' | 'collecting' | 'ready_for_review' | 'approved'

export interface ProductPlan {
  id: ProductTierId
  name: string
  eyebrow: string
  shortDescription: string
  monthlyPrice: number | null
  buyoutPrice: number | null
  termMonths: number | null
  idealFor: string
  featured?: boolean
  features: string[]
  boltOns: string[]
}

export interface ProductCatalogItem {
  id: string
  label: string
  description: string
  reason: string
  category: 'standard' | 'addon' | 'integration' | 'commerce' | 'ai' | 'seo'
  included_tiers: ProductTierId[]
  suggested_tiers: ProductTierId[]
  monthly_price: number
  once_off_price: number
  active: boolean
  customer_visible: boolean
  position: number
}

export interface PricedCatalogItem extends ProductCatalogItem {
  original_price: number
  adjusted_price: number
  discount_amount: number
  discount_label?: string
}

export interface AddOnPricingSummary {
  items: PricedCatalogItem[]
  originalTotal: number
  discountedTotal: number
  discountTotal: number
}

const sharedBackendItemIds = new Set([
  'online_shop_system',
  'payment_portal',
  'customer_accounts',
  'database',
  'cms_shop',
  'crm',
  'invoicing',
  'ai_suite',
  'guided_intake',
])

export const SHARED_BACKEND_DISCOUNT_RATE = 0.4

export const productPlans: ProductPlan[] = [
  {
    id: 'launch',
    name: 'Launch',
    eyebrow: 'Bespoke front-end site',
    shortDescription:
      'A custom-designed website built around your brand, your content, and the exact first impression your business needs.',
    monthlyPrice: 799,
    buyoutPrice: 7670,
    termMonths: 12,
    idealFor: 'Founders and small businesses that need a beautiful public website without backend complexity.',
    features: [
      'Custom visual direction, layout, and responsive front-end build',
      'Core pages shaped around your offer, trust signals, and calls to action',
      'Performance, accessibility, SEO basics, and launch handover',
      'Designed to be upgraded later with auth, database, payments, or guided workflows',
    ],
    boltOns: ['Domain purchase', 'Hosting', 'Database', 'Backend/API features', 'Auth and integrations'],
  },
  {
    id: 'growth',
    name: 'Growth',
    eyebrow: 'Website plus content engine',
    shortDescription:
      'Everything in Launch, plus a social scheduling layer so your weekly posts can be prepared in one focused session.',
    monthlyPrice: 999,
    buyoutPrice: 9590,
    termMonths: 12,
    idealFor: 'Businesses that need the website and the content rhythm to start working together.',
    featured: true,
    features: [
      'Everything included in Launch',
      'Social media post planner and scheduling dashboard foundation',
      'Weekly content workflow built around campaigns, offers, and announcements',
      'Upgrade path for approvals, caption drafts, and multi-platform publishing',
    ],
    boltOns: ['Platform API integrations', 'Caption generation', 'Team approvals', 'Analytics', 'Asset library'],
  },
  {
    id: 'command',
    name: 'Command',
    eyebrow: 'Business operating system',
    shortDescription:
      'A premium website and backend that can become the operating system for the business.',
    monthlyPrice: 3499,
    buyoutPrice: 25200,
    termMonths: 12,
    idealFor: 'Teams that want a serious business system: CRM, invoicing, shop, records, dashboards, and guided workflows.',
    features: [
      'Everything in Growth',
      'Custom backend, database, roles, admin tools, and secure member areas',
      'Intelligent workflows for client intake, support, operations, and internal requests',
      'Optional modules for CRM, invoicing, online shop, reporting, Google/Facebook login, and automation',
    ],
    boltOns: ['CRM', 'Invoicing', 'Online shop', 'Google auth', 'Facebook auth', 'Custom automation'],
  },
]

export const fallbackCatalogItems: ProductCatalogItem[] = [
  {
    id: 'custom_art',
    label: 'Custom Art',
    description: 'Purpose-made visual assets for the site instead of generic stock filler.',
    reason: 'Included because the brand needs a visual signature, not a template.',
    category: 'standard',
    included_tiers: ['launch', 'growth', 'command'],
    suggested_tiers: [],
    monthly_price: 0,
    once_off_price: 0,
    active: true,
    customer_visible: true,
    position: 10,
  },
  {
    id: 'custom_design',
    label: 'Custom Design',
    description: 'A designed interface system shaped around the business, offer, and customer journey.',
    reason: 'Standard on every package because the product is bespoke.',
    category: 'standard',
    included_tiers: ['launch', 'growth', 'command'],
    suggested_tiers: [],
    monthly_price: 0,
    once_off_price: 0,
    active: true,
    customer_visible: true,
    position: 20,
  },
  {
    id: 'technical_seo',
    label: 'Search & Indexing Foundation',
    description: 'Metadata, sitemap hygiene, robots setup, preview cards, and launch checks for Google indexing.',
    reason: 'Included so the site is built with proper discovery and sharing basics from day one.',
    category: 'seo',
    included_tiers: ['launch', 'growth', 'command'],
    suggested_tiers: [],
    monthly_price: 0,
    once_off_price: 0,
    active: true,
    customer_visible: true,
    position: 30,
  },
  {
    id: 'custom_preview_cards',
    label: 'Custom Link Preview Cards',
    description: 'Share cards for WhatsApp, Facebook, LinkedIn, X, and search previews.',
    reason: 'Included because the first impression often happens before the visitor reaches the site.',
    category: 'seo',
    included_tiers: ['launch', 'growth', 'command'],
    suggested_tiers: [],
    monthly_price: 0,
    once_off_price: 0,
    active: true,
    customer_visible: true,
    position: 40,
  },
  {
    id: 'domain_hosting',
    label: 'Domain & Hosting Setup',
    description: 'Domain purchase guidance, DNS setup, hosting connection, and launch-ready configuration.',
    reason: 'Suggested when the customer needs us to buy/register, connect, or manage the domain and hosting setup.',
    category: 'addon',
    included_tiers: [],
    suggested_tiers: ['launch', 'growth', 'command'],
    monthly_price: 0,
    once_off_price: 0,
    active: true,
    customer_visible: true,
    position: 48,
  },
  {
    id: 'database',
    label: 'Managed Database',
    description: 'Structured data for accounts, orders, content, products, and portal records.',
    reason: 'Suggested when the customer needs login, checkout, content management, or a business dashboard.',
    category: 'addon',
    included_tiers: ['command'],
    suggested_tiers: ['growth'],
    monthly_price: 250,
    once_off_price: 2500,
    active: true,
    customer_visible: true,
    position: 50,
  },
  {
    id: 'contact_form',
    label: 'Smart Contact Form',
    description: 'A clean enquiry form with structured lead capture and delivery to the right inbox.',
    reason: 'Suggested when the website needs enquiries, quote requests, bookings, or support messages.',
    category: 'addon',
    included_tiers: ['command'],
    suggested_tiers: ['launch', 'growth'],
    monthly_price: 150,
    once_off_price: 1500,
    active: true,
    customer_visible: true,
    position: 55,
  },
  {
    id: 'payment_portal',
    label: 'Payment Portal',
    description: 'Checkout, order records, payment status, and customer account flow.',
    reason: 'Suggested when the business needs deposits, subscriptions, products, invoices, or online sales.',
    category: 'integration',
    included_tiers: ['command'],
    suggested_tiers: ['growth'],
    monthly_price: 350,
    once_off_price: 3500,
    active: true,
    customer_visible: true,
    position: 60,
  },
  {
    id: 'online_shop_system',
    label: 'Online Shop System',
    description: 'A custom product catalogue, editable shop CMS, Supabase data layer, and payment portal.',
    reason: 'Suggested when customers need to browse, order, or pay for products on the website.',
    category: 'commerce',
    included_tiers: [],
    suggested_tiers: ['launch', 'growth', 'command'],
    monthly_price: 600,
    once_off_price: 6000,
    active: true,
    customer_visible: true,
    position: 62,
  },
  {
    id: 'customer_accounts',
    label: 'Customer Accounts',
    description: 'Secure login, profiles, roles, and a protected client area for customers or staff.',
    reason: 'Suggested when visitors need to sign in, track orders, access private content, or manage details.',
    category: 'integration',
    included_tiers: ['command'],
    suggested_tiers: ['growth'],
    monthly_price: 300,
    once_off_price: 3000,
    active: true,
    customer_visible: true,
    position: 65,
  },
  {
    id: 'guided_intake',
    label: 'Guided Intake Assistant',
    description: 'A conversational intake that turns customer answers into a clean build specification.',
    reason: 'Suggested when the brief is complex or the customer needs help shaping the project.',
    category: 'ai',
    included_tiers: ['command'],
    suggested_tiers: ['growth'],
    monthly_price: 450,
    once_off_price: 4500,
    active: true,
    customer_visible: true,
    position: 70,
  },
  {
    id: 'ai_suite',
    label: 'AI Suite',
    description: 'Custom AI assistant flows for intake, support, internal actions, and guided customer experiences.',
    reason: 'Suggested when the business wants conversational help, automation, or intelligent workflows inside the site.',
    category: 'ai',
    included_tiers: [],
    suggested_tiers: ['launch', 'growth', 'command'],
    monthly_price: 400,
    once_off_price: 4000,
    active: true,
    customer_visible: true,
    position: 72,
  },
  {
    id: 'social_scheduler',
    label: 'Social Scheduler',
    description: 'Plan, prepare, and schedule a week of posts from one focused dashboard.',
    reason: 'Included in Growth because the site should help the business keep publishing after launch.',
    category: 'integration',
    included_tiers: ['growth', 'command'],
    suggested_tiers: [],
    monthly_price: 0,
    once_off_price: 0,
    active: true,
    customer_visible: true,
    position: 75,
  },
  {
    id: 'cms_shop',
    label: 'CMS / Shop',
    description: 'Editable content, products, collections, checkout paths, and store-ready structure.',
    reason: 'Suggested when the business needs to manage stock, services, articles, products, or landing pages.',
    category: 'commerce',
    included_tiers: [],
    suggested_tiers: ['command'],
    monthly_price: 650,
    once_off_price: 6500,
    active: true,
    customer_visible: true,
    position: 80,
  },
  {
    id: 'crm',
    label: 'CRM Foundation',
    description: 'A simple customer record system for leads, notes, statuses, and follow-ups.',
    reason: 'Suggested when the business needs to track clients, jobs, quotes, or service history.',
    category: 'addon',
    included_tiers: [],
    suggested_tiers: ['command'],
    monthly_price: 550,
    once_off_price: 5500,
    active: true,
    customer_visible: true,
    position: 90,
  },
  {
    id: 'invoicing',
    label: 'Invoicing Module',
    description: 'Quote and invoice records connected to customers, payments, and internal workflow.',
    reason: 'Suggested when the business wants operational tools beyond a public website.',
    category: 'addon',
    included_tiers: [],
    suggested_tiers: ['command'],
    monthly_price: 500,
    once_off_price: 5000,
    active: true,
    customer_visible: true,
    position: 100,
  },
]

export const getProductPlan = (id: ProductTierId) => productPlans.find((plan) => plan.id === id)

export const formatRand = (amount: number | null) =>
  amount == null ? 'Custom quote' : `R${amount.toLocaleString('en-ZA')}`

export const getPlanPrice = (plan: ProductPlan, billingMode: BillingMode) =>
  billingMode === 'monthly' ? plan.monthlyPrice : plan.buyoutPrice

export const isSharedBackendItem = (item: ProductCatalogItem) =>
  sharedBackendItemIds.has(item.id) || ['commerce', 'ai', 'integration'].includes(item.category)

export const calculateAddOnPricing = (
  items: ProductCatalogItem[],
  billingMode: BillingMode,
): AddOnPricingSummary => {
  const sharedBackendItems = items.filter((item) => isSharedBackendItem(item));
  const foundationItemId = sharedBackendItems.length
    ? sharedBackendItems.reduce((foundation, item) => (item.position < foundation.position ? item : foundation)).id
    : null

  const pricedItems = items.map((item) => {
    const originalPrice = billingMode === 'monthly' ? item.monthly_price : item.once_off_price
    const shouldDiscount = Boolean(foundationItemId && item.id !== foundationItemId && isSharedBackendItem(item) && originalPrice > 0)
    const discountAmount = shouldDiscount ? Math.round(originalPrice * SHARED_BACKEND_DISCOUNT_RATE) : 0

    return {
      ...item,
      original_price: originalPrice,
      adjusted_price: Math.max(0, originalPrice - discountAmount),
      discount_amount: discountAmount,
      discount_label: shouldDiscount ? 'Shared Supabase foundation' : undefined,
    }
  })

  const originalTotal = pricedItems.reduce((total, item) => total + item.original_price, 0)
  const discountedTotal = pricedItems.reduce((total, item) => total + item.adjusted_price, 0)

  return {
    items: pricedItems,
    originalTotal,
    discountedTotal,
    discountTotal: originalTotal - discountedTotal,
  }
}

export const intakeStarterQuestions = [
  'What does your business sell, and who is the ideal customer?',
  'Which pages or sections must the website include on day one?',
  'What should the website make visitors do: call, book, buy, enquire, or sign up?',
  'Which existing brands, websites, or visuals feel close to the direction?',
  'Do you need add-ons such as domain, hosting, database, auth, payments, CRM, invoicing, or online shop?',
]
